import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { ErrorMessage, useFormikContext } from 'formik';
import React from 'react';
import { Card, Col, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/rootReducer';
import { CreateApplicationFormValues } from '../../views/ApplicationCreate';

const ApplicationPersonCreate = (): React.ReactElement => {
  const { t } = useTranslation(['createApplication', 'common']);
  const { unions, unionsLoading, unionsError } = useSelector((state: RootState) => state.createApplication);
  const { values, errors, touched, getFieldProps, setFieldValue } = useFormikContext<CreateApplicationFormValues>();

  return (
    <Card className="mb-2">
      <Card.Body>
        <Form.Row>
          <Col>
            <h4>{t('applicant')}</h4>
          </Col>
        </Form.Row>
        <Form.Row>
          <Col md="4">
            <p className="small text-muted">{t('applicantHelp')}</p>
          </Col>
          <Col>
            <Form.Group controlId="name">
              <Form.Label>{t('name')}*:</Form.Label>
              <Form.Control
                type="text"
                className={classNames({
                  'is-invalid': touched.name && errors.name,
                })}
                readOnly
                {...getFieldProps('name')}
              />
              <Form.Text className="text-muted">{t('applicantName')}</Form.Text>
              <ErrorMessage name="name" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>

            <Form.Group controlId="ssn">
              <Form.Label>{t('ssn')}*:</Form.Label>
              <Form.Control
                type="text"
                className={classNames({
                  'is-invalid': touched.ssn && errors.ssn,
                })}
                readOnly
                {...getFieldProps('ssn')}
              />
              <Form.Text className="text-muted">{t('applicantSsn')}</Form.Text>
              <ErrorMessage name="ssn" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>

            <Form.Group controlId="address">
              <Form.Label>{t('address')}:</Form.Label>
              <Form.Control
                type="text"
                className={classNames({
                  'is-invalid': touched.address && errors.address,
                })}
                {...getFieldProps('address')}
              />
              <Form.Text className="text-muted">{t('applicantAddress')}</Form.Text>
            </Form.Group>

            <Form.Group controlId="email">
              <Form.Label>{t('email')}*:</Form.Label>
              <Form.Control
                type="email"
                className={classNames({
                  'is-invalid': touched.email && errors.email,
                })}
                {...getFieldProps('email')}
              />
              <Form.Text className="text-muted">{t('applicantEmail')}</Form.Text>
              <ErrorMessage name="email" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>
            <Form.Group controlId="mobile">
              <Form.Label>{t('mobile')}*:</Form.Label>
              <Form.Control
                type="text"
                className={classNames({
                  'is-invalid': touched.mobile && errors.mobile,
                })}
                {...getFieldProps('mobile')}
              />
              <Form.Text className="text-muted">{t('applicantMobile')}</Form.Text>
              <ErrorMessage name="mobile" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>

            <Form.Group controlId="union">
              <Form.Label>{t('union')}:</Form.Label>

              <Form.Row className="pb-2">
                <Col md="3">
                  <Form.Check
                    custom
                    id="hasNoUnion"
                    name="hasNoUnion"
                    label={t('hasNoUnion')}
                    inline
                    type="checkbox"
                    checked={values.hasNoUnion === true}
                    onChange={(e) => {
                      setFieldValue('hasNoUnion', e.target.checked ? true : undefined);
                      setFieldValue('dontKnowUnion', undefined);
                    }}
                  />
                </Col>
                <Col md="2">
                  <Form.Check
                    custom
                    id="dontKnowUnion"
                    name="dontKnowUnion"
                    label={t('dontKnowUnion')}
                    inline
                    type="checkbox"
                    checked={values.dontKnowUnion === true}
                    onChange={(e) => {
                      setFieldValue('dontKnowUnion', e.target.checked ? true : undefined);
                      setFieldValue('hasNoUnion', undefined);
                    }}
                  />
                </Col>
              </Form.Row>
              {!(values.dontKnowUnion || values.hasNoUnion) && (
                <Form.Control
                  as="select"
                  className={classNames({
                    'is-invalid': touched.union && errors.union,
                  })}
                  {...getFieldProps('union')}
                >
                  <option value="">{t('chooseUnion')}</option>
                  {unions &&
                    unions.map((union) => (
                      <option key={union.id} value={union.id}>
                        {union.name}
                      </option>
                    ))}
                </Form.Control>
              )}
              {unionsError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
              {unionsLoading === 'idle' && unions?.length === 0 && <div className="invalid-feedback d-block text-left">{t('noUnions')}</div>}
              <Form.Text className="text-muted">{unionsError === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}</Form.Text>
              <ErrorMessage name="union" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>
          </Col>
        </Form.Row>
      </Card.Body>
    </Card>
  );
};
export default ApplicationPersonCreate;
